import request from './request'

export interface LLMProviderConfig {
  api_base?: string | null
  api_key?: string | null
  model_chat?: string | null
  model_analysis?: string | null
  model_review?: string | null
}

export interface MediaProviderConfig {
  api_base?: string | null
  api_key?: string | null
  api_secret?: string | null
  model?: string | null
  priority_models?: string[] | null
}

export interface ComplianceProviderConfig {
  api_base?: string | null
  api_key?: string | null
  secret?: string | null
  model?: string | null
}

export interface TTSProviderConfig {
  api_base?: string | null
  api_key?: string | null
  api_secret?: string | null
  app_key?: string | null
  region?: string | null
  voice_id?: string | null
  model?: string | null
}

export interface APIKeyConfig {
  llm_backend: 'openclaw' | 'dify' | 'hybrid'
  llm_provider: string
  llm_api_key?: string | null
  llm_api_base?: string | null
  llm_model_chat?: string | null
  llm_model_analysis?: string | null
  llm_model_review?: string | null
  dify_api_base?: string | null
  dify_api_key?: string | null
  dify_workflow_analysis?: string | null
  dify_workflow_script?: string | null
  dify_workflow_review?: string | null
  image_gen_provider: string
  image_gen_api_base?: string | null
  image_gen_api_key?: string | null
  image_gen_api_secret?: string | null
  image_gen_model?: string | null
  image_gen_model_priority?: string[] | null
  tts_provider: string
  tts_api_base?: string | null
  tts_api_key?: string | null
  tts_api_secret?: string | null
  tts_app_key?: string | null
  tts_region?: string | null
  tts_voice_id?: string | null
  tts_model?: string | null
  video_gen_provider: string
  video_gen_api_base?: string | null
  video_gen_api_key?: string | null
  video_gen_api_secret?: string | null
  video_gen_model?: string | null
  video_gen_model_priority?: string[] | null
  compliance_provider: string
  compliance_api_base?: string | null
  compliance_api_key?: string | null
  compliance_secret?: string | null
  compliance_model?: string | null
  oss_endpoint?: string | null
  oss_bucket_name?: string | null
  oss_access_key_id?: string | null
  oss_access_key_secret?: string | null
  llm_provider_pool: Record<string, LLMProviderConfig>
  image_provider_pool: Record<string, MediaProviderConfig>
  tts_provider_pool: Record<string, TTSProviderConfig>
  video_provider_pool: Record<string, MediaProviderConfig>
  compliance_provider_pool: Record<string, ComplianceProviderConfig>
}

export interface APIKeyUpdate extends Partial<APIKeyConfig> {
  llm_api_key?: string
}

export interface ModelCatalogItem {
  id: string
  label: string
  model: string
  provider: string
  description?: string | null
  capability?: string | null
  enabled: boolean
  is_default: boolean
  operations: string[]
}

export interface ModelCatalogConfig {
  text_models: ModelCatalogItem[]
  image_models: ModelCatalogItem[]
  video_models: ModelCatalogItem[]
  compliance_models: ModelCatalogItem[]
}

export interface ConfigItem {
  config_key: string
  config_value: string | null
  description?: string
}

export interface IntegrationStatusItem {
  key: string
  label: string
  provider: string
  mode?: string | null
  implemented: boolean
  configured: boolean
  ready: boolean
  detail: string
}

export const configApi = {
  getAPIKeys: () => request.get<APIKeyConfig>('/configs/api-keys'),
  getIntegrationStatus: () => request.get<IntegrationStatusItem[]>('/configs/integration-status'),
  getModelCatalogs: () => request.get<ModelCatalogConfig>('/configs/model-catalogs'),
  updateModelCatalogs: (data: ModelCatalogConfig) =>
    request.put<{ success: boolean; message: string; data?: ModelCatalogConfig }>('/configs/model-catalogs', data),
  getModelOptions: () => request.get<ModelCatalogConfig>('/configs/model-options'),
  updateAPIKeys: (data: APIKeyUpdate) =>
    request.put<{ success: boolean; message: string; data?: APIKeyConfig }>('/configs/api-keys', data),
  getConfigs: () => request.get<ConfigItem[]>('/configs'),
  getConfig: (key: string) => request.get<ConfigItem>(`/configs/${key}`),
  setConfig: (data: { config_key: string; config_value: string; description?: string; is_sensitive?: boolean }) =>
    request.post<ConfigItem>('/configs', data),
  deleteConfig: (key: string) => request.delete(`/configs/${key}`),
}
